import React, { useMemo, useState } from 'react';
import styles from './ContactPage.module.css';
import { submitContact } from '../api/contact';

const initialForm = {
  name: '',
  email: '',
  inquiry_type: '一般のお問い合わせ',
  subject: '',
  message: '',
  event_url: '',
};

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [inquiryCode, setInquiryCode] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = useMemo(
    () => [
      { value: '一般のお問い合わせ', label: '一般のお問い合わせ' },
      { value: 'チケット購入について', label: 'チケット購入について' },
      { value: '販売者登録・売上について', label: '販売者登録・売上について' },
      { value: '不正・違反報告', label: '不正・違反報告' },
      { value: 'その他', label: 'その他' },
    ],
    []
  );

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const next = {};

    if (!form.name.trim()) next.name = 'お名前を入力してください。';
    if (!form.email.trim()) {
      next.email = 'メールアドレスを入力してください。';
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = 'メールアドレスの形式が正しくありません。';
    }

    if (!form.inquiry_type.trim()) {
      next.inquiry_type = 'お問い合わせ種別を選択してください。';
    }

    if (form.subject && form.subject.length > 255) {
      next.subject = '件名は255文字以内で入力してください。';
    }

    if (!form.message.trim()) {
      next.message = 'お問い合わせ内容を入力してください。';
    } else if (form.message.trim().length < 10) {
      next.message = 'お問い合わせ内容は10文字以上で入力してください。';
    }

    if (form.event_url && !/^https?:\/\//.test(form.event_url)) {
      next.event_url = 'イベントURLは http:// または https:// から入力してください。';
    }

    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setInquiryCode('');

    const nextErrors = validate();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitted(false);
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        inquiry_type: form.inquiry_type,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        event_url: form.event_url.trim() || null,
      });

      setSubmitted(true);
      setInquiryCode(result?.inquiry_code || '');
      setForm(initialForm);
      setFieldErrors({});
    } catch (err) {
      setSubmitted(false);
      const backendErrors = err?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        const normalized = {};
        Object.entries(backendErrors).forEach(([key, messages]) => {
          normalized[key] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setFieldErrors(normalized);
      } else {
        setSubmitError('送信に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>お問い合わせ</h2>
      <p className={styles.description}>
        IRIE TIXに関するご質問・ご要望はこちらからお送りください。
      </p>

      {submitted && (
        <p className={styles.success}>
          お問い合わせを受け付けました。<br />
          お問い合わせ番号: {inquiryCode || '発行中'}<br />
          受付確認メールを送信しましたのでご確認ください。
        </p>
      )}

      {submitError && <p className={styles.error}>{submitError}</p>}

      <form onSubmit={handleSubmit}>
        <label className={styles.label} htmlFor="contact-name">
          お名前
        </label>
        <input
          id="contact-name"
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          className={`${styles.input} ${fieldErrors.name ? styles.inputError : ''}`.trim()}
          placeholder="山田 太郎"
        />
        {fieldErrors.name && <p className={styles.fieldError}>{fieldErrors.name}</p>}

        <label className={styles.label} htmlFor="contact-email">
          メールアドレス
        </label>
        <input
          id="contact-email"
          type="email"
          value={form.email}
          onChange={(e) => onChange('email', e.target.value)}
          className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`.trim()}
          placeholder="example@irietix.com"
        />
        {fieldErrors.email && <p className={styles.fieldError}>{fieldErrors.email}</p>}

        <label className={styles.label} htmlFor="contact-category">
          お問い合わせ種別
        </label>
        <select
          id="contact-category"
          value={form.inquiry_type}
          onChange={(e) => onChange('inquiry_type', e.target.value)}
          className={`${styles.select} ${fieldErrors.inquiry_type ? styles.inputError : ''}`.trim()}
        >
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {fieldErrors.inquiry_type && <p className={styles.fieldError}>{fieldErrors.inquiry_type}</p>}

        <label className={styles.label} htmlFor="contact-subject">
          件名（任意）
        </label>
        <input
          id="contact-subject"
          type="text"
          value={form.subject}
          onChange={(e) => onChange('subject', e.target.value)}
          className={`${styles.input} ${fieldErrors.subject ? styles.inputError : ''}`.trim()}
          placeholder="例: 決済エラーについて"
        />
        {fieldErrors.subject && <p className={styles.fieldError}>{fieldErrors.subject}</p>}

        <label className={styles.label} htmlFor="contact-message">
          お問い合わせ内容
        </label>
        <textarea
          id="contact-message"
          value={form.message}
          onChange={(e) => onChange('message', e.target.value)}
          className={`${styles.textarea} ${fieldErrors.message ? styles.inputError : ''}`.trim()}
          placeholder="お問い合わせ内容をご記入ください（10文字以上）"
        />
        {fieldErrors.message && <p className={styles.fieldError}>{fieldErrors.message}</p>}

        <label className={styles.label} htmlFor="contact-event-url">
          イベントURL（任意）
        </label>
        <input
          id="contact-event-url"
          type="url"
          value={form.event_url}
          onChange={(e) => onChange('event_url', e.target.value)}
          className={`${styles.input} ${fieldErrors.event_url ? styles.inputError : ''}`.trim()}
          placeholder="https://irietix.com/events/sample-event"
        />
        {fieldErrors.event_url && <p className={styles.fieldError}>{fieldErrors.event_url}</p>}

        <p className={styles.notice}>
          通常2〜3営業日以内にご返信いたします。内容により返信にお時間をいただく場合があります。
        </p>

        <button type="submit" className={styles.submitButton} disabled={submitting}>
          {submitting ? '送信中...' : '送信する'}
        </button>
      </form>
    </div>
  );
};

export default ContactPage;
